//#region IMPORTS
import { useEffect, useMemo, useState } from "react";
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import {
  fetchInstoreOrderAsync,
  instoreOrdersSelectors,
} from "./instoreOrdersSlice";
import LoadingSpinner from "../../components/loading/LoadingSpinner";
import NotFound from "../../components/errors/NotFound";
import GoBackButton from "../../components/genericButtons/goBackButton/GoBackButton";
import ConditionalParagraph from "../../components/genericTextLine/ConditionalParagraph";
import { formatPrice } from "../../helpers/formatPrice";
import formatArticleName from "../../helpers/formatArticleName";
//#endregion

type SortKey = "name" | "quantity";
type SortOrder = "asc" | "desc";

function SortableHeader({
  label,
  active,
  order,
  onClick,
}: {
  label: string;
  active: boolean;
  order: SortOrder;
  onClick: () => void;
}) {
  return (
    <th
      scope="col"
      onClick={onClick}
      className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900 cursor-pointer"
    >
      <div className="flex gap-1 items-center">
        <span className="font-bold text-emerald-900 animate-pulse hover:text-gray-400">
          {label}
        </span>
        {active &&
          (order === "desc" ? (
            <ArrowDownCircleIcon className="w-5 h-5 text-emerald-500" />
          ) : (
            <ArrowUpCircleIcon className="w-5 h-5 text-emerald-500" />
          ))}
      </div>
    </th>
  );
}

export default function UserInstoreOrderDetailPage() {
  //#region SETUP
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const instoreOrder = useAppSelector((state) =>
    instoreOrdersSelectors.selectById(state, id),
  );
  const { loading } = useAppSelector(({ instoreOrders }) => instoreOrders);

  // Local sort state
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortKey(null);
      setSortOrder("asc");
    }
  };

  const sortedLines = useMemo(() => {
    const lines = instoreOrder?.instore_order_lines ?? [];
    if (!sortKey) return lines;
    const copy = [...lines];
    copy.sort((a, b) => {
      const av = sortKey === "name" ? a.article_name ?? "" : a.amount;
      const bv = sortKey === "name" ? b.article_name ?? "" : b.amount;
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [instoreOrder, sortKey, sortOrder]);
  //#endregion

  //#region LOGIC
  useEffect(() => {
    if (id) dispatch(fetchInstoreOrderAsync(Number(id)));
  }, [id, dispatch]);
  //#endregion

  //#region FAIL SAFE
  if (loading && !instoreOrder) return <LoadingSpinner />;
  if (!instoreOrder) return <NotFound />;
  //#endregion

  const hasComment =
    !!instoreOrder.comment &&
    instoreOrder.comment !== "no comment" &&
    instoreOrder.comment !== "Initial order";

  //#region RENDER
  return (
    <div className="overflow-x-auto">
      <section className="pb-12">
        {/* Header */}
        <header className="border-b-2 border-slate-900 mb-8">
          <div className="flex justify-between gap-4 pl-2 mb-4">
            <GoBackButton />

            <div className="grow">
              <p className="text-2xl lg:text-4xl">
                <b>{instoreOrder.store_name}</b>
              </p>
              <p className="text-xl lg:text-3xl">{instoreOrder.order_code}</p>
              <p className="text-lg lg:text-2xl">
                {instoreOrder.store_language}
              </p>
            </div>

            {/* Store info */}
            <div className="text-left lg:text-right font-sm text-slate-700">
              <p>
                <strong>{instoreOrder.order_type_instore_name}</strong>
              </p>
              <p>
                Rituals Cosmetics / {instoreOrder.external_store_id}
              </p>
              <ConditionalParagraph
                value={instoreOrder.delivery_addres_street_1}
              />
              <ConditionalParagraph
                value={instoreOrder.delivery_addres_street_2 ?? ""}
              />
              <ConditionalParagraph
                value={instoreOrder.delivery_addres_street_3 ?? ""}
              />
              <ConditionalParagraph
                inline
                value={instoreOrder.delivery_addres_zip_code}
              />
              <ConditionalParagraph
                inline
                margin="0 0 0 0.5rem"
                value={instoreOrder.delivery_addres_city}
              />
              <p>{instoreOrder.delivery_addres_country}</p>
              <p>
                {t("rituals.orderDate")}:{" "}
                {instoreOrder.order_date &&
                  instoreOrder.order_date.toString().split(" ")[0]}
              </p>
            </div>
          </div>

          {/* Comment box */}
          {hasComment && (
            <article className="orders-detail-page__header__comment-box">
              <h2>
                {t("rituals.extraComment")}:{" "}
                <strong>{instoreOrder.comment}</strong>
              </h2>
            </article>
          )}

          {/* Tracking info — nothing shown when there is no tracking code */}
          {instoreOrder.track_and_trace && (
            <div className="mb-2">
              <h2>
                <strong>{t("rituals.trackingCode") ?? "Tracking"}:</strong>{" "}
                <a
                  href={instoreOrder.track_and_trace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-700 hover:text-pink-500 underline"
                >
                  {instoreOrder.track_and_trace}
                </a>
              </h2>
            </div>
          )}
        </header>

        {/* Table */}
        {sortedLines.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <SortableHeader
                  label={t("rituals.name")}
                  active={sortKey === "name"}
                  order={sortOrder}
                  onClick={() => toggleSort("name")}
                />
                <th
                  scope="col"
                  className="whitespace-nowrap pr-4 py-3.5 text-left text-xs font-bold text-emerald-900"
                >
                  {t("rituals.setDepth")}
                </th>
                <th
                  scope="col"
                  className="whitespace-nowrap pr-4 py-3.5 text-left text-xs font-bold text-emerald-900"
                >
                  {t("rituals.price")} ({t("rituals.inEuro")})
                </th>
                <SortableHeader
                  label={t("rituals.quantity")}
                  active={sortKey === "quantity"}
                  order={sortOrder}
                  onClick={() => toggleSort("quantity")}
                />
              </tr>
            </thead>
            <tbody className="bg-white">
              {sortedLines.map((line) => (
                <tr key={line.id} className="even:bg-gray-100">
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">
                        {formatArticleName(line.article_name) || "—"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {line.theme_name}
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                    {typeof line.set === "number" ? line.set : "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                    {typeof line.price === "number" ? (
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {formatPrice(line.price * line.amount)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatPrice(line.price)} × {line.amount}
                        </span>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                    {line.amount}
                  </td>
                </tr>
              ))}
            </tbody>
            {typeof instoreOrder.total_price === "number" && (
              <tfoot>
                <tr className="border-t-2 border-slate-900">
                  <td
                    colSpan={2}
                    className="px-3 py-4 text-right text-sm font-bold text-gray-900"
                  >
                    {t("rituals.totalPrice")}
                  </td>
                  <td className="px-3 py-4 text-sm font-bold text-gray-900">
                    {formatPrice(instoreOrder.total_price)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        ) : (
          <h2>{t("rituals.zeroItems")}</h2>
        )}
      </section>
    </div>
  );
  //#endregion
}
